import React, { useEffect, useState } from "react";
import { Loader } from "lucide-react";
import { useAutoConversionLimits, useSetAutoConversionLimits } from "../hooks/useAutoConversion";

const money = (v) => Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// The most one automatic conversion may be, and the most converted in a day across all partners (USD).
// A conversion that needs more is not made automatically: the payout carries on through the next provider and,
// for the first-choice provider, treasury is asked to approve it (Partner Conversion, approvals).
function AutoConversionLimits() {
  const { data, isLoading, isError, error } = useAutoConversionLimits();
  const { mutate: save, isPending } = useSetAutoConversionLimits();
  const [maxUsd, setMaxUsd] = useState("");
  const [dailyMaxUsd, setDailyMaxUsd] = useState("");

  useEffect(() => {
    if (data) {
      setMaxUsd(String(data.maxUsd));
      setDailyMaxUsd(String(data.dailyMaxUsd));
    }
  }, [data]);

  if (isLoading) return null;
  if (isError) return <p className="mb-3 text-sm text-red-600">Could not load the limits: {error.message}</p>;

  const changed = Number(maxUsd) !== data.maxUsd || Number(dailyMaxUsd) !== data.dailyMaxUsd;
  const handleSave = () => {
    const next = { maxUsd: Number(maxUsd), dailyMaxUsd: Number(dailyMaxUsd) };
    if (
      !window.confirm(
        `Set the automatic conversion limits to ${money(next.maxUsd)} USD per conversion and ${money(next.dailyMaxUsd)} USD per day?\n\nReal money is converted automatically up to these amounts. Anything bigger goes to treasury for approval instead.`,
      )
    ) {
      return;
    }
    save(next);
  };

  const field = (label, value, setValue, ceiling, fallback) => (
    <label className="text-sm text-gray-700">
      <span className="block mb-1 text-xs font-semibold tracking-wider text-gray-500 uppercase">{label}</span>
      <div className="flex gap-2 items-center">
        <span className="text-gray-500">USD</span>
        <input
          type="number"
          min="1"
          max={ceiling}
          step="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="px-3 py-1.5 w-32 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <span className="block mt-1 text-xs text-gray-400">
        Default {money(fallback)}, at most {money(ceiling)}
      </span>
    </label>
  );

  return (
    <div className="p-3 mb-4 bg-white rounded-lg border border-gray-200">
      <p className="mb-2 text-sm font-semibold text-gray-800">Limits</p>
      <div className="flex flex-wrap gap-6 items-start">
        {field("Per conversion", maxUsd, setMaxUsd, data.ceilings.maxUsd, data.defaults.maxUsd)}
        {field("Per day, all partners", dailyMaxUsd, setDailyMaxUsd, data.ceilings.dailyMaxUsd, data.defaults.dailyMaxUsd)}
        <div className="self-end">
          <button
            onClick={handleSave}
            disabled={!changed || isPending}
            className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl transition-all hover:bg-[#012126] disabled:opacity-40"
          >
            {isPending && <Loader className="w-4 h-4 animate-spin" />}
            Save limits
          </button>
        </div>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Converted so far today: {money(data.usedTodayUsd)} of {money(data.dailyMaxUsd)} USD. Only super admins can change
        the limits.
      </p>
    </div>
  );
}

export default AutoConversionLimits;
